/** One-shot, below-the-fold reveals. Content stays readable without JS and
 * reduced-motion users never receive a hidden starting state. */
export function bindWebsiteMotion(): void {
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (motion.matches || !('IntersectionObserver' in window)) return;
  const sections = Array.from(document.querySelectorAll<HTMLElement>('[data-reveal]'));
  const pending = sections.filter(section => section.getBoundingClientRect().top >= window.innerHeight);
  const observer = new IntersectionObserver(entries => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      entry.target.classList.remove('reveal-pending');
      observer.unobserve(entry.target);
    }
  }, { threshold: 0, rootMargin: '0px 0px -24px 0px' });
  for (const section of pending) {
    section.classList.add('reveal-pending');
    observer.observe(section);
  }
  motion.addEventListener('change', event => {
    if (!event.matches) return;
    observer.disconnect();
    for (const section of pending) section.classList.remove('reveal-pending');
  }, { once: true });
}

export function bindSiteMenus(): void {
  const menus = Array.from(document.querySelectorAll<HTMLDetailsElement>('.site-help.site-more'));
  if (!menus.length) return;
  const syncExpanded = (menu: HTMLDetailsElement): void => {
    menu.querySelector('summary')?.setAttribute('aria-expanded', String(menu.open));
  };
  const restoreFocus = (target: HTMLElement | null): void => {
    if (!target || !target.isConnected || target.closest('.hidden') || target.closest('[aria-hidden="true"]')) return;
    target.focus({ preventScroll: true });
  };
  for (const menu of menus) {
    syncExpanded(menu);
    menu.addEventListener('toggle', () => syncExpanded(menu));
    menu.addEventListener('click', (event) => {
      if ((event.target as HTMLElement).closest('a')) menu.open = false;
    });
  }
  document.addEventListener('click', (event) => {
    for (const menu of menus) {
      if (menu.open && !menu.contains(event.target as Node)) menu.open = false;
    }
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const openMenu = menus.find(menu => menu.open);
    if (!openMenu) return;
    event.preventDefault();
    openMenu.open = false;
    restoreFocus(openMenu.querySelector<HTMLElement>('summary'));
  });
}
